import './table.css';
import React, { useState, useEffect } from 'react';
import './modal.css';
import { createPortal } from 'react-dom';
import { Checkbox } from './checkbox';
import { DropdownMenu } from './dropdown';
import { Popup } from './Popup';
import { Button } from './Buttons';
import { getImageUrl } from '../firebase/firebase-verify';

function isObject(A) {
    return (typeof A === 'object' || typeof A === 'function') && A !== null;
}

function isArray(A) {
    return Array.isArray(A);
}

function getImageIds(el) {
    if (!el) return [];
    if (Array.isArray(el)) return el.filter(Boolean);
    if (typeof el === 'object') {
        const values = Object.values(el);
        const first = values[0];
        if (Array.isArray(first)) return first.filter(Boolean);
        return values.filter((v) => typeof v === 'string' && v);
    }
    return [];
}

export function DragTable({ click, title, data, options = [] }) {
    const [selectedRows, setSelectedRows] = useState([]);
    const [rows, setRows] = useState([]);
    const [highlight, setHighlight] = useState();
    const [headers, setHeaders] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState(false);
    const [draggedRow, setDraggedRow] = useState(null);
    const [topOffset, setTopOffset] = useState([0, 0]);

    const handleClick = click
        ? async (e, p, i) => {
            await click(p.id, p, e);
            setHighlight(i);
        }
        : false;

    const handleAdd = data.add
        ? async () => {
            await data.add();
            setRowData();
        }
        : false;

    const handleUpdate = data.update
        ? async (e, d) => {
            await data.update(e, d);
            setRowData();
        }
        : false;

    const handleDelete = data.delete
        ? async (e, del = selectedRows) => {
            await data.delete(del);
            setRowData();
            e.stopPropagation();
            e.preventDefault();
        }
        : false;

    useEffect(() => {
        setRowData();
    }, [data]);

    function setRowData() {
        const d = data?.data ? data.data.slice() : [];
        setRows(
            d.sort((a, b) => {
                if (!isNaN(a.index) && !isNaN(b.index)) {
                    return a.index - b.index;
                }
                return 0;
            })
        );
    }

    function handleCheckboxChange(projectId) {
        if (selectedRows.includes(projectId)) {
            setSelectedRows(selectedRows.filter((id) => id !== projectId));
        } else {
            setSelectedRows([...selectedRows, projectId]);
        }
    }

    useEffect(() => {
        const fields = data?.fields || [];

        function handleColumnSelect(fieldId, column) {
            const find = headers.find((c) => c[0] === fieldId);
            if (find) {
                setHeaders(headers.filter((col) => col[0] !== fieldId));
            } else {
                setHeaders([...headers, [fieldId, column]]);
            }
        }

        const t = fields.map((h, i) => (
            <div key={'field' + i}>
                <Checkbox
                    value={h.name}
                    type="slider"
                    checked={headers.findIndex((c) => c[0] === h.id) >= 0}
                    onChange={() => handleColumnSelect(h.id, h.name)}
                />
            </div>
        ));

        setModalContent(<div className={'header' + headers.length}>{t}</div>);
    }, [headers, data?.fields]);

    useEffect(() => {
        const fields = data?.fields || [];
        setHeaders(
            fields
                .filter((f) => f.columnVisible)
                .map((f) => [f.id, f.name, f.type])
        );
    }, [data?.fields]);

    function getRowContent(project) {
        return headers.map((h, i) => {
            const k = project.id + 'fgd' + i;
            const el = project.data ? project.data[h[0]] : undefined;
            const value = check(el);

            if (h[2] === 'ImageUpload' || h[2] === 'Image') {
                const src = (el && el.medium) || getImageUrl(project.id, 'small');
                return (
                    <td className="img-cell" key={k}>
                        <div style={{ height: '153px', background: '#bdbdbd' }}>
                            {src && <img src={src} alt="" />}
                        </div>
                    </td>
                );
            }



            if (h[2] === "UploadImages") {
                const ids = isObject(el) ? Object.values(el)[0] : null;
                return (
                    <td className='img-cell' key={k}>
                        <div style={{ display: 'flex' }}>
                            {Array.isArray(ids) && ids.map(e => {
                                const ele = getImageUrl(e, 'small');
                                return (
                                    <div key={e} style={{ width: '60px', height: '60px', padding: '4px' }}>
                                        <img style={{ objectFit: 'cover' }} src={ele} alt="" />
                                    </div>
                                );
                            })}
                        </div>
                    </td>
                );
            }

            if (value === true || value === false) {
                return (
                    <td style={{ opacity: 0.4 }} key={k}>
                        <Checkbox float="center" checked={value} onChange={() => { }} />
                    </td>
                );
            }

            return <td key={k}>{value}</td>;
        });

        function check(p) {
            if (p == null) return '';
            if (isArray(p)) {
                if (p.length && !isObject(p[0])) return p.join(', ');
                return p
                    .map((x) => (isObject(x) ? Object.values(x).join(', ') : x))
                    .join(', ');
            }
            if (isObject(p)) return Object.values(p).join(', ');
            return p;
        }
    }

    function saveOrder() {
        const up = {};
        rows.forEach((p, i) => {
            up[p.id] = { index: i };
        });
        if (data.update) data.update(up);
    }

    function handleDragStart(e, project) {
        setDraggedRow(project);
        setTopOffset([e.clientY, 0]);
    }

    function handleDrop(e) {
        if (draggedRow) {
            setTopOffset([e.clientY, 0]);
            setDraggedRow(null);
            saveOrder();
        }
    }

    function handleDragOver(e) {
        if (!draggedRow) return;
        const nt = e.clientY - topOffset[0];
        if (e.clientY === 0) return;

        const proj = draggedRow;
        const oli = rows.indexOf(proj);
        if (oli === 0 && nt < 0) return;
        if (oli === rows.length - 1 && nt > 0) return;

        const ntabs = Math.abs(nt);
        const inde = nt / ntabs;
        const element = e.currentTarget.parentElement;
        const hoverElement = inde < 0 ? element.previousSibling : element.nextSibling;
        if (!hoverElement) return;

        const he = hoverElement.clientHeight;
        if (ntabs >= he) {
            const newProjects = [...rows];
            newProjects.splice(oli, 1);
            newProjects.splice(oli + inde, 0, proj);
            setRows(newProjects);
            setTopOffset([e.clientY, 0]);
            return;
        }
        setTopOffset([topOffset[0], nt]);
    }

    const sel = selectedRows.length > 0;

    return (
        <div className="TableOverview" key="tableOverview">
            {rows && (
                <div key="dragtable">
                    {title && <div className="title">{title}</div>}
                    <div className="actions">
                        {handleAdd && <Button text={'Toevoegen'} click={handleAdd} />}
                        {handleDelete && (
                            <Button
                                text={'Verwijder'}
                                active={sel}
                                click={sel ? handleDelete : () => { }}
                            />
                        )}


                        <Button text={'Kolommen'} style="alternate" click={() => setShowModal(true)} />
                        {showModal &&
                            createPortal(
                                <Popup
                                    title="Set visible columns"
                                    content={modalContent}
                                    size="small"
                                    onClose={() => setShowModal(false)}
                                />,
                                document.body
                            )}
                    </div>
                    <div key="scroll-bar-container" className="scroll-container">
                        <table>
                            <thead>
                                <tr>
                                    {handleDelete && <th style={{ width: '50px' }}></th>}
                                    <th style={{ width: '50px' }}></th>
                                    {headers.map((h, i) => (
                                        <th key={'header' + i} style={{ width: 'auto' }}>
                                            <div>
                                                <span>{h[1]}</span>
                                                <i>:</i>
                                            </div>
                                        </th>
                                    ))}
                                    {options && <th></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((project, index) => {
                                    if (!project?.id) return null;

                                    const opt = [
                                        ...options
                                            .filter(Boolean)
                                            .map((e) => ({
                                                title: e.title,
                                                func: (ev) => e.func(ev, project.id),
                                            })),
                                    ];

                                    if (handleDelete) {
                                        opt.push({
                                            func: (e) => handleDelete(e, project.id),
                                            title: 'Delete',
                                        });
                                    }
                                    if (handleClick) {
                                        opt.unshift({
                                            func: (e) => handleClick(e, project, data.fields),
                                            title: 'Open',
                                        });
                                    }

                                    return (
                                        <tr
                                            className={`drag-row ${!draggedRow && 'static'} ${highlight === index ? 'visited' : ''
                                                }`}
                                            key={'tablenumber' + project.id}
                                            style={
                                                draggedRow === project
                                                    ? {
                                                        background: '#bbb',
                                                        top: topOffset[1] + 'px',
                                                        position: 'relative',
                                                        zIndex: 10,
                                                    }
                                                    : {}
                                            }
                                            onClick={(e) => handleClick && handleClick(e, project, index)}
                                        >
                                            <td
                                                className={'drag-handle ' + (draggedRow === project ? 'active' : '')}
                                                draggable={true}
                                                onDragStart={(e) => handleDragStart(e, project)}
                                                onDrag={handleDragOver}
                                                onDragEnd={handleDrop}
                                                key={project.id}
                                                style={draggedRow === project ? { cursor: 'grab' } : {}}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <div />
                                                <div className="border-element">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="grey">
                                                        <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"></path>
                                                    </svg>
                                                </div>
                                            </td>
                                            {handleDelete && (
                                                <td>
                                                    <Checkbox
                                                        checked={selectedRows.includes(project.id)}
                                                        onChange={() => handleCheckboxChange(project.id)}
                                                    />
                                                </td>
                                            )}
                                            {getRowContent(project)}
                                            {options && (
                                                <td>
                                                    <DropdownMenu key={'dropdown' + project.id} content={opt} />
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}